/**
 * SettingsZohoCard — Zoho Desk Email Integration card.
 * Extracted from SettingsPage.jsx (Iter 140, Feb 2026).
 *
 * Props mirror the parent's local state; OAuth code exchange + reauthorize
 * handlers stay in SettingsPage so cross-cutting state (loading flags,
 * status refresh, toasts) is unchanged.
 */
import { MessageSquare, Loader2, CheckCircle, AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";

// Zoho OAuth scope (kept in sync with backend's expected scopes; safe to
// hard-code here because it's a public constant, not a secret).
const ZOHO_SCOPE_STRING = "Desk.tickets.ALL,Desk.contacts.READ,Desk.basic.READ,Desk.settings.READ,Desk.search.READ,Desk.tasks.READ";

// Local helper for the OAuth code-paste flow.
const ZohoAuthForm = ({ zohoCode, setZohoCode, onSubmit, loading }) => (
  <div className="flex gap-2">
    <input
      type="text"
      value={zohoCode}
      onChange={(e) => setZohoCode(e.target.value)}
      placeholder="Paste Grant Token (Code) — e.g. 1000.xxxxxxxx.yyyyyyyy"
      className="flex-1 px-3 py-2 border rounded text-xs font-mono"
      data-testid="zoho-code-input"
    />
    <button
      onClick={onSubmit}
      disabled={loading || !zohoCode}
      className="btn-primary text-xs px-4 disabled:opacity-50"
      data-testid="zoho-authorize-btn"
    >
      {loading ? "Authorizing…" : "Authorize"}
    </button>
  </div>
);

export default function SettingsZohoCard({
  user,
  zohoStatus, zohoCode, setZohoCode,
  zohoAuthLoading, handleZohoExchange, handleZohoReauthorize, fetchZohoStatus,
}) {
  return (
    <>
        {/* Zoho Desk Email Integration */}
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <div className="bento-card" data-testid="zoho-settings-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-[#F26B00]/10 rounded-md">
                <MessageSquare size={20} className="text-[#F26B00]" />
              </div>
              <h2
                className="text-xl font-medium text-[#1C1917]"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Zoho Desk (Email Integration)
              </h2>
              {zohoStatus?.authorized && (
                <span className="ml-auto text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">✓ Connected</span>
              )}
            </div>

            {zohoStatus?.authorized ? (
              <div className="space-y-3">
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-900 space-y-1" data-testid="zoho-connected-info">
                  <div><span className="font-bold">Org ID:</span> {zohoStatus.org_id || <span className="text-red-600">⚠ Not set in backend .env (ZOHO_ORG_ID)</span>}</div>
                  <div><span className="font-bold">Data Center:</span> zoho.{zohoStatus.data_center}</div>
                  <div><span className="font-bold">Authorized by:</span> {zohoStatus.authorized_by}</div>
                  <div><span className="font-bold">Departments:</span> {zohoStatus.departments?.length || 0} found {zohoStatus.departments?.length > 0 && `— ${zohoStatus.departments.map(d => d.name).join(", ")}`}</div>
                </div>
                <button
                  onClick={fetchZohoStatus}
                  className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
                  data-testid="zoho-refresh-status"
                >
                  <RefreshCw size={14} /> Refresh status
                </button>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-900">
                  <p className="font-bold mb-1">Getting a "scope" error (e.g. UPDATE scope required)?</p>
                  <p className="mb-2">Click the button below — it copies the full scope string (<code className="bg-white px-1 rounded">Desk.tickets.<b>ALL</b></code> + read scopes) and opens Zoho's API console. Generate a new 10-min code with that scope and paste it in the form below.</p>
                  <details>
                    <summary className="cursor-pointer font-medium">Show scope string</summary>
                    <code className="block mt-1 text-[10px] bg-white border border-amber-300 rounded px-1.5 py-1 break-all">{ZOHO_SCOPE_STRING}</code>
                  </details>
                </div>
                <button
                  onClick={handleZohoReauthorize}
                  className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                  data-testid="zoho-reauthorize-btn"
                >
                  <ExternalLink size={14} /> Copy scope & open Zoho API console
                </button>
                <details className="text-xs text-[#57534E]" open>
                  <summary className="cursor-pointer font-medium">Paste new Grant Token</summary>
                  <div className="mt-2">
                    <ZohoAuthForm zohoCode={zohoCode} setZohoCode={setZohoCode} onSubmit={handleZohoExchange} loading={zohoAuthLoading} />
                  </div>
                </details>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900 space-y-2">
                  <p className="font-bold">One-time setup (30 sec):</p>
                  <ol className="list-decimal list-inside space-y-1 pl-1">
                    <li>Click the button below — copies full scope + opens Zoho API console.</li>
                    <li>In your Self Client → Generate Code tab, paste the scope, set duration to 10 minutes, click Create.</li>
                    <li>Copy the code Zoho shows, paste it in the Grant Token field, click Authorize.</li>
                  </ol>
                </div>
                <button
                  onClick={handleZohoReauthorize}
                  className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                  data-testid="zoho-authorize-btn-full"
                >
                  <ExternalLink size={14} /> Copy scope & open Zoho API console
                </button>
                <ZohoAuthForm zohoCode={zohoCode} setZohoCode={setZohoCode} onSubmit={handleZohoExchange} loading={zohoAuthLoading} />
                <p className="text-[11px] text-[#A8A29E] italic">
                  Also ensure <code className="text-[10px]">ZOHO_ORG_ID</code> is set in backend <code className="text-[10px]">.env</code>. Find it in the URL when you click any ticket in Zoho Desk.
                </p>
              </div>
            )}
          </div>
        )}
    </>
  );
}
